import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAuctionSettings,
  fetchTournamentById,
  fetchTournaments,
  saveAuctionSettings,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import {
  getAuctionManagerPath as getCommunityAuctionManagerPath,
  getCommunityId,
} from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { buildPublicAuctionUrl, getPublicOnlineAuctionPath } from '../../../utils/auction/publicAuctionLinks';

const defaultForm = {
  auctionDateTime: '',
  pursePerTeam: '',
  baseBid: '',
  bidIncrement: '',
  playersPerTeam: '',
  auctionProcess: 'auto',
  distributeFinalUnsoldPlayers: false,
};

const defaultNumberOfTeams = 2;
const createTeam = (index, defaultPurse = '') => ({
  name: `Team ${index + 1}`,
  logoUrl: '',
  ownerId: '',
  ownerPlayerId: '',
  initialPurse: defaultPurse,
  captain: '',
  viceCaptain: '',
  wicketKeeper: '',
  playerIds: [],
  players: [],
  retainers: [],
});

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const iso = date.toISOString();
  return iso.slice(0, 16);
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const OnlineAuctionSetupPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { auctionManagerId, organizationId, communityId: routeCommunityId, clubId: routeClubId, tournamentId } = useParams();
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !routeCommunityId && !routeClubId);
  const communityId = getCommunityId(routeCommunityId);
  const clubId = getClubId(routeClubId);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(() => tournamentId || '');
  const [formState, setFormState] = useState(defaultForm);
  const [numberOfTeams, setNumberOfTeams] = useState(defaultNumberOfTeams);
  const [teams, setTeams] = useState(() => Array.from({ length: defaultNumberOfTeams }, (_, index) => createTeam(index)));
  const [teamSearchTerms, setTeamSearchTerms] = useState(
    () => Array.from({ length: defaultNumberOfTeams }, () => '')
  );
  const [activeTeamIndex, setActiveTeamIndex] = useState(0);
  const [isLoadingTournaments, setIsLoadingTournaments] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [playerError, setPlayerError] = useState('');
  const [copyLinkStatus, setCopyLinkStatus] = useState('');

  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value._id) return String(value._id);
    if (value.id) return String(value.id);
    return String(value);
  };

  const currentUserId = normalizeId(user?._id || user?.id);

  const hasIndividualAuctionAccess = (tournament) => {
    const ownerUserId = normalizeId(tournament?.ownerUser);
    const createdById = normalizeId(tournament?.createdBy);
    const auctionManagerUserId = normalizeId(tournament?.auctionManager);
    const additionalAuctionManagers = Array.isArray(tournament?.auctionManagers) ? tournament.auctionManagers : [];
    return ownerUserId === currentUserId
      || createdById === currentUserId
      || auctionManagerUserId === currentUserId
      || additionalAuctionManagers.some((manager) => normalizeId(manager) === currentUserId);
  };

  const selectedTournament = useMemo(
    () => tournaments.find((tournament) => tournament.id === selectedTournamentId),
    [selectedTournamentId, tournaments]
  );
  const mappedPlayers = useMemo(
    () =>
      tournamentPlayers.map((player) => ({
        id: player.id || player._id || '',
        name: player.name || player.fullName || player.personal?.fullName || 'Unnamed player',
        email: player.email || player.user?.email || '',
        image: player.image || player.profilePhoto || player.media?.profilePhoto || '',
      })),
    [tournamentPlayers]
  );

  const playerLookup = useMemo(() => new Map(mappedPlayers.map((player) => [player.id, player])), [mappedPlayers]);

  const getTeamPlayerIds = (team) => {
    const explicitIds = Array.isArray(team?.playerIds) ? team.playerIds : [];
    const playerObjectIds = Array.isArray(team?.players)
      ? team.players.map((player) => (typeof player === 'string' ? player : player?.id || player?._id)).filter(Boolean)
      : [];
    const retainerIds = Array.isArray(team?.retainers) ? team.retainers : [];
    return Array.from(new Set([...explicitIds, ...playerObjectIds, ...retainerIds].filter(Boolean)));
  };

  const getPlayerName = (playerId) => playerLookup.get(playerId)?.name || playerId || 'Not selected';

  const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  useEffect(() => {
    setSelectedTournamentId(tournamentId || '');
  }, [tournamentId]);

  useEffect(() => {
  }, [isIndividualRoute, tournamentId]);

  useEffect(() => {
    setActiveTeamIndex((previous) => {
      if (!numberOfTeams) return 0;
      return Math.min(previous, Math.max(0, numberOfTeams - 1));
    });
  }, [numberOfTeams]);

  useEffect(() => {
    if (!token) {
      setErrorMessage('Sign in to configure an online auction.');
      return;
    }

    const loadTournaments = async () => {
      setIsLoadingTournaments(true);
      setErrorMessage('');

      try {
        let response;
        if (isIndividualRoute) {
          const tournamentResponse = await fetchTournamentById(tournamentId, token);
          const tournament = tournamentResponse?.tournament || null;
          response = { tournaments: tournament ? [tournament] : [] };
        } else {
          response = await fetchTournaments(token, organizationId, { communityId, clubId });
        }
        const availableTournaments = (response.tournaments || [])
          .map((tournament) => ({ id: tournament.id || tournament._id, name: tournament.name || 'Untitled tournament' }))
          .filter((tournament) => Boolean(tournament.id));

        setTournaments(availableTournaments);

        if (!selectedTournamentId && availableTournaments.length > 0) {
          const defaultTournament = availableTournaments[0];
          setSelectedTournamentId(defaultTournament.id);
          const updatedAuctionManagementPath = isIndividualRoute
            ? `/individual-tournament/${defaultTournament.id}/auction`
            : communityId
              ? getCommunityAuctionManagerPath(communityId, auctionManagerId, defaultTournament.id)
              : clubId
                ? getClubAuctionManagerPath(clubId, auctionManagerId, defaultTournament.id)
                : getAuctionManagerPath(organizationId, auctionManagerId, defaultTournament.id);
          if (updatedAuctionManagementPath) {
            navigate(`${updatedAuctionManagementPath}/online`, { replace: true });
          }
        }
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load tournaments right now.');
      } finally {
        setIsLoadingTournaments(false);
      }
    };

    loadTournaments();
  }, [auctionManagerId, clubId, communityId, isIndividualRoute, navigate, organizationId, selectedTournamentId, token, tournamentId]);

  useEffect(() => {
    if (!selectedTournamentId || !token) {
      setTournamentPlayers([]);
      return;
    }

    const loadPlayers = async () => {
      setIsLoadingPlayers(true);
      setPlayerError('');

      try {
        const tournamentResponse = await fetchTournamentById(
          selectedTournamentId,
          token,
          organizationId,
          {
            communityId,
            clubId,
            activeContextType: isIndividualRoute ? 'individual' : undefined,
            ownerType: isIndividualRoute ? 'individual' : undefined,
          },
        );
        const loadedTournament = tournamentResponse?.tournament || {};
        if (isIndividualRoute && !hasIndividualAuctionAccess(loadedTournament)) {
          setPlayerError('You do not have access to this individual tournament auction.');
          setTournamentPlayers([]);
          return;
        }
        setTournamentPlayers(loadedTournament?.players || []);
      } catch (error) {
        setTournamentPlayers([]);
        setPlayerError(error.message || 'Unable to load players for this tournament.');
      } finally {
        setIsLoadingPlayers(false);
      }
    };

    loadPlayers();
  }, [clubId, communityId, currentUserId, isIndividualRoute, organizationId, selectedTournamentId, token]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedTournamentId || !token) return;

      setIsLoadingSettings(true);
      setErrorMessage('');
      setStatusMessage('');

      try {
        const response = await fetchAuctionSettings(selectedTournamentId, token);
        const settings = response.settings;

        if (!settings) {
          setFormState(defaultForm);
          setNumberOfTeams(defaultNumberOfTeams);
          setTeams(Array.from({ length: defaultNumberOfTeams }, (_, index) => createTeam(index)));
          setTeamSearchTerms(Array.from({ length: defaultNumberOfTeams }, () => ''));
          return;
        }

        setFormState({
          auctionDateTime: formatDateTimeLocal(settings.auctionDateTime),
          pursePerTeam: settings.pursePerTeam ?? '',
          baseBid: settings.baseBid ?? '',
          bidIncrement: settings.bidIncrement ?? '',
          playersPerTeam: settings.playersPerTeam ?? '',
          auctionProcess: settings.auctionProcess || 'auto',
          distributeFinalUnsoldPlayers: settings.distributeFinalUnsoldPlayers === true,
        });

        const incomingTeams = (settings.teams || []).map((team, index) => ({
          name: team.name || `Team ${index + 1}`,
          logoUrl: team.logoUrl || '',
          ownerId: team.ownerPlayerId || team.ownerId || '',
          ownerPlayerId: team.ownerPlayerId || team.ownerId || '',
          initialPurse: team.initialPurse ?? settings.pursePerTeam ?? '',
          captain: team.captain || '',
          viceCaptain: team.viceCaptain || '',
          wicketKeeper: team.wicketKeeper || '',
          playerIds: Array.isArray(team.playerIds) ? team.playerIds : Array.isArray(team.players) ? team.players.map((player) => (typeof player === 'string' ? player : player.id || player._id)).filter(Boolean) : Array.isArray(team.retainers) ? team.retainers : [],
          players: Array.isArray(team.players) ? team.players : [],
          retainers: Array.isArray(team.retainers) ? team.retainers : [],
        }));
        const incomingCount = Math.max(1, settings.numberOfTeams || incomingTeams.length || defaultNumberOfTeams);

        setNumberOfTeams(incomingCount);
        setTeams(
          incomingTeams.length
            ? incomingTeams.slice(0, incomingCount || incomingTeams.length)
            : Array.from({ length: incomingCount }, (_, index) => createTeam(index))
        );
        setTeamSearchTerms(Array.from({ length: incomingCount || defaultNumberOfTeams }, () => ''));
      } catch (error) {
        setFormState(defaultForm);
        setNumberOfTeams(defaultNumberOfTeams);
        setTeams(Array.from({ length: defaultNumberOfTeams }, (_, index) => createTeam(index)));
        setTeamSearchTerms(Array.from({ length: defaultNumberOfTeams }, () => ''));
        setErrorMessage(error.message || 'Unable to load auction settings right now.');
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [selectedTournamentId, token]);

  const handleTournamentChange = (event) => {
    const newTournamentId = event.target.value;
    setSelectedTournamentId(newTournamentId);
    const updatedAuctionManagementPath =
      clubId
        ? getClubAuctionManagerPath(clubId, auctionManagerId, newTournamentId)
        : getAuctionManagerPath(organizationId, auctionManagerId, newTournamentId);
    if (updatedAuctionManagementPath) {
      navigate(`${updatedAuctionManagementPath}/online`);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((previous) => ({ ...previous, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleTeamCountChange = (event) => {
    const nextCount = Math.max(1, Math.min(12, Number(event.target.value) || defaultNumberOfTeams));
    setNumberOfTeams(nextCount);
    setTeams((previous) => {
      if (previous.length === nextCount) return previous;

      if (nextCount > previous.length) {
        const additions = Array.from({ length: nextCount - previous.length }, (_, index) =>
          createTeam(previous.length + index, formState.pursePerTeam)
        );
        return [...previous, ...additions];
      }

      return previous.slice(0, nextCount);
    });
    setTeamSearchTerms((previous) => {
      if (nextCount > previous.length) {
        return [...previous, ...Array.from({ length: nextCount - previous.length }, () => '')];
      }

      return previous.slice(0, nextCount);
    });
  };

  const updateTeamField = (teamIndex, field, value) => {
    setTeams((previous) => previous.map((team, index) => (index === teamIndex ? { ...team, [field]: value } : team)));
  };

  const handleTeamLogoChange = (teamIndex, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload an image for the team logo.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      updateTeamField(teamIndex, 'logoUrl', event.target?.result || '');
    };
    reader.readAsDataURL(file);
  };

  const toggleTeamPlayerSelection = (teamIndex, playerId) => {
    setTeams((previous) =>
      previous.map((team, index) => {
        if (index !== teamIndex) return team;

        const playerIds = new Set(getTeamPlayerIds(team));
        if (playerIds.has(playerId)) {
          playerIds.delete(playerId);
        } else {
          playerIds.add(playerId);
        }

        const nextPlayerIds = Array.from(playerIds);
        return {
          ...team,
          playerIds: nextPlayerIds,
          retainers: nextPlayerIds,
          players: nextPlayerIds,
          captain: nextPlayerIds.includes(team.captain) ? team.captain : '',
          viceCaptain: nextPlayerIds.includes(team.viceCaptain) ? team.viceCaptain : '',
          wicketKeeper: nextPlayerIds.includes(team.wicketKeeper) ? team.wicketKeeper : '',
        };
      })
    );
  };

  const filteredPlayers = (teamIndex) => {
    const searchTerm = (teamSearchTerms[teamIndex] || '').toLowerCase();
    return mappedPlayers.filter((player) => {
      if (!searchTerm) return true;

      const haystack = `${player.name || ''} ${player.email || ''}`.toLowerCase();
      return haystack.includes(searchTerm);
    });
  };

  const handleCaptainChange = (teamIndex, newCaptainId) => {
    updateTeamField(teamIndex, 'captain', newCaptainId);
  };

  const handleOwnerChange = (teamIndex, newOwnerId) => {
    setTeams((previous) =>
      previous.map((team, index) => {
        if (index !== teamIndex) return team;
        return { ...team, ownerId: newOwnerId, ownerPlayerId: newOwnerId };
      }),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTournamentId || !token) return;

    setIsSaving(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const tournamentPlayerIds = new Set(mappedPlayers.map((player) => player.id).filter(Boolean));
      const normalizedTeams = teams
        .slice(0, numberOfTeams)
        .map((team, index) => {
          const playerIds = getTeamPlayerIds(team).filter((playerId) => tournamentPlayerIds.has(playerId));
          return {
            name: team.name?.trim?.() || `Team ${index + 1}`,
            logoUrl: team.logoUrl || '',
            ownerId: team.ownerPlayerId || team.ownerId || '',
            ownerPlayerId: team.ownerPlayerId || team.ownerId || '',
            initialPurse: team.initialPurse === '' || team.initialPurse === undefined ? Number(formState.pursePerTeam) || 0 : Number(team.initialPurse),
            captain: team.captain || '',
            viceCaptain: team.viceCaptain || '',
            wicketKeeper: team.wicketKeeper || '',
            playerIds,
            players: playerIds,
            retainers: playerIds,
          };
        });
      for (let index = 0; index < normalizedTeams.length; index += 1) {
        const team = normalizedTeams[index];
        const assigned = new Set(team.playerIds);
        if (!team.name.trim()) {
          setErrorMessage(`Team ${index + 1}: Team name is required.`);
          setIsSaving(false);
          return;
        }
        if (team.ownerPlayerId && !tournamentPlayerIds.has(team.ownerPlayerId)) {
          setErrorMessage(`Team ${team.name}: owner player must be selected from tournament registered players.`);
          setIsSaving(false);
          return;
        }
        if (!team.playerIds.length) {
          setErrorMessage(`Team ${team.name}: Please select at least one player.`);
          setIsSaving(false);
          return;
        }
        if (!team.captain || !assigned.has(team.captain)) {
          setErrorMessage(`Team ${team.name}: Captain must be selected from assigned players.`);
          setIsSaving(false);
          return;
        }
        if (!team.viceCaptain || !assigned.has(team.viceCaptain)) {
          setErrorMessage(`Team ${team.name}: Vice Captain must be selected from assigned players.`);
          setIsSaving(false);
          return;
        }
        if (!team.wicketKeeper || !assigned.has(team.wicketKeeper)) {
          setErrorMessage(`Team ${team.name}: Wicket Keeper must be selected from assigned players.`);
          setIsSaving(false);
          return;
        }
      }

      const payload = {
        ...formState,
        pursePerTeam: formState.pursePerTeam === '' ? 0 : Number(formState.pursePerTeam),
        baseBid: formState.baseBid === '' ? 0 : Number(formState.baseBid),
        bidIncrement: formState.bidIncrement === '' ? 0 : Number(formState.bidIncrement),
        playersPerTeam: Number(formState.playersPerTeam),
        auctionProcess: formState.auctionProcess || 'auto',
        distributeFinalUnsoldPlayers: formState.distributeFinalUnsoldPlayers === true,
        numberOfTeams,
        teams: normalizedTeams,
      };

      const response = await saveAuctionSettings(selectedTournamentId, payload, token);
      const settings = response.settings || {};
      setStatusMessage('Auction settings saved for this tournament.');
      setFormState({
        auctionDateTime: formatDateTimeLocal(settings.auctionDateTime) || formState.auctionDateTime,
        pursePerTeam: settings.pursePerTeam ?? formState.pursePerTeam,
        baseBid: settings.baseBid ?? formState.baseBid,
        bidIncrement: settings.bidIncrement ?? formState.bidIncrement,
        playersPerTeam: settings.playersPerTeam ?? formState.playersPerTeam,
        auctionProcess: settings.auctionProcess || formState.auctionProcess || 'auto',
        distributeFinalUnsoldPlayers: settings.distributeFinalUnsoldPlayers === true,
      });
      const incomingTeams = (settings.teams || payload.teams || []).map((team, index) => ({
        name: team.name || `Team ${index + 1}`,
        logoUrl: team.logoUrl || '',
        ownerId: team.ownerPlayerId || team.ownerId || '',
        ownerPlayerId: team.ownerPlayerId || team.ownerId || '',
        initialPurse: team.initialPurse ?? payload.pursePerTeam ?? '',
        captain: team.captain || '',
        viceCaptain: team.viceCaptain || '',
        wicketKeeper: team.wicketKeeper || '',
        playerIds: Array.isArray(team.playerIds) ? team.playerIds : Array.isArray(team.players) ? team.players : Array.isArray(team.retainers) ? team.retainers : [],
        players: Array.isArray(team.players) ? team.players : [],
        retainers: Array.isArray(team.retainers) ? team.retainers : [],
      }));
      const updatedCount = Math.max(1, settings.numberOfTeams || numberOfTeams || incomingTeams.length || defaultNumberOfTeams);
      setNumberOfTeams(updatedCount);
      setTeams(
        incomingTeams.length
          ? incomingTeams.slice(0, updatedCount || incomingTeams.length)
          : Array.from({ length: updatedCount || defaultNumberOfTeams }, (_, index) => createTeam(index))
      );
      setTeamSearchTerms(Array.from({ length: updatedCount || defaultNumberOfTeams }, () => ''));
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save auction settings right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const auctionManagementPath =
    (isIndividualRoute
      ? `/individual-tournament/${selectedTournamentId || tournamentId}/auction`
      : communityId
        ? getCommunityAuctionManagerPath(communityId, auctionManagerId, selectedTournamentId || tournamentId)
        : clubId
          ? getClubAuctionManagerPath(clubId, auctionManagerId, selectedTournamentId || tournamentId)
          : getAuctionManagerPath(organizationId, auctionManagerId, selectedTournamentId || tournamentId)) ||
    '/management/auction';
  const teamOwnerAdminPath = `${auctionManagementPath}/online/team-owner-admins`;
  const publicAuctionPath = getPublicOnlineAuctionPath(selectedTournamentId);
  const publicAuctionUrl = useMemo(() => buildPublicAuctionUrl(selectedTournament || selectedTournamentId), [selectedTournament, selectedTournamentId]);

  const handleCopyPublicLink = async () => {
    if (!publicAuctionUrl) {
      setCopyLinkStatus('Select a tournament to copy the public link.');
      return;
    }

    try {
      await copyTextToClipboard(publicAuctionUrl);
      setCopyLinkStatus('Public link copied');
    } catch (_error) {
      setCopyLinkStatus('Unable to copy the public link.');
    }
  };

  const handleSharePublicLink = async () => {
    if (!publicAuctionUrl) {
      setCopyLinkStatus('Select a tournament to share the public link.');
      return;
    }

    if (navigator.share) {
      await navigator.share({ title: 'Public auction watch link', url: publicAuctionUrl });
      return;
    }

    await handleCopyPublicLink();
  };

  return (
    <div className="py-4 d-grid gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex flex-column gap-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-primary-subtle text-primary-emphasis">Auction Manager</span>
            {selectedTournament?.name ? <span className="text-muted small">{selectedTournament.name}</span> : null}
          </div>
          <h1 className="h4 mb-0">Online auction setup</h1>
          <p className="text-muted mb-0">Choose your tournament and finalize the digital auction rules.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Link to={teamOwnerAdminPath} className="btn btn-primary">
            Manage team owner admins
          </Link>
          <Link
            to={publicAuctionPath || '#'}
            className={`btn btn-outline-secondary ${!publicAuctionPath ? 'disabled' : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!publicAuctionPath}
            onClick={(event) => {
              if (!publicAuctionPath) event.preventDefault();
            }}
          >
            Preview
          </Link>
          <button type="button" className="btn btn-outline-primary" onClick={handleCopyPublicLink} disabled={!publicAuctionPath}>
            Copy
          </button>
          <button type="button" className="btn btn-outline-success" onClick={handleSharePublicLink} disabled={!publicAuctionUrl}>
            Share Public Link
          </button>
          <Link to={auctionManagementPath} className="btn btn-outline-primary">
            Back to auction management
          </Link>
        </div>
      </div>
      {copyLinkStatus ? <p className="small mb-0 text-muted">{copyLinkStatus}</p> : null}

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-semibold text-muted" htmlFor="tournamentSelect">
                Tournament
              </label>
              <select
                id="tournamentSelect"
                className="form-select"
                value={selectedTournamentId}
                onChange={handleTournamentChange}
                disabled={isLoadingTournaments}
              >
                <option value="">Select a tournament</option>
                {tournaments.map((tournament) => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
              {isLoadingTournaments ? <small className="text-muted">Loading tournaments…</small> : null}
            </div>
            <div className="col-12 col-md-6 col-lg-8 text-md-end d-flex flex-column align-items-md-end align-items-start gap-2">
              <p className="mb-0 text-muted small">
                Online auctions let you set the purse, bidding ladder, and roster size before inviting bidders.
              </p>
              <Link className="btn btn-outline-secondary" to={`${auctionManagementPath}/teams`}>
                Configure teams
              </Link>
            </div>
          </div>

          <hr className="text-muted" />

          <form className="row g-4" onSubmit={handleSubmit}>
            <div className="col-12 col-lg-6">
              <label className="form-label fw-semibold" htmlFor="auctionDateTime">
                Auction date &amp; time
              </label>
              <input
                id="auctionDateTime"
                type="datetime-local"
                name="auctionDateTime"
                value={formState.auctionDateTime}
                onChange={handleInputChange}
                className="form-control"
                disabled={!selectedTournamentId || isLoadingSettings}
              />
              <small className="text-muted">Share this with bidders to lock in the live session.</small>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold" htmlFor="pursePerTeam">
                Purse per team
              </label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  id="pursePerTeam"
                  type="number"
                  min="0"
                  step="0.01"
                  name="pursePerTeam"
                  value={formState.pursePerTeam}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled={!selectedTournamentId || isLoadingSettings}
                />
              </div>
              <small className="text-muted">Total spending power for each franchise.</small>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold" htmlFor="baseBid">
                Base bid
              </label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  id="baseBid"
                  type="number"
                  min="0"
                  step="0.01"
                  name="baseBid"
                  value={formState.baseBid}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled={!selectedTournamentId || isLoadingSettings}
                />
              </div>
              <small className="text-muted">Starting price for eligible players.</small>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold" htmlFor="bidIncrement">
                Bid increase by
              </label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  id="bidIncrement"
                  type="number"
                  min="0"
                  step="0.01"
                  name="bidIncrement"
                  value={formState.bidIncrement}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled={!selectedTournamentId || isLoadingSettings}
                />
              </div>
              <small className="text-muted">Increment applied to each new bid.</small>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold" htmlFor="playersPerTeam">
                Players per team
              </label>
              <input
                id="playersPerTeam"
                type="number"
                min="1"
                max="30"
                step="1"
                name="playersPerTeam"
                value={formState.playersPerTeam}
                onChange={handleInputChange}
                className="form-control"
                disabled={!selectedTournamentId || isLoadingSettings}
                required
              />
              <small className="text-muted">Roster cap for each squad.</small>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold" htmlFor="auctionProcess">
                Auction process
              </label>
              <select
                id="auctionProcess"
                name="auctionProcess"
                className="form-select"
                value={formState.auctionProcess}
                onChange={handleInputChange}
                disabled={!selectedTournamentId || isLoadingSettings}
              >
                <option value="auto">Auto</option>
                <option value="manual">Manual</option>
              </select>
              <small className="text-muted">Choose whether bidding advances automatically or manually.</small>
            </div>

            <div className="col-12">
              <div className="form-check form-switch border rounded-3 p-3 ps-5 bg-white">
                <input
                  id="distributeFinalUnsoldPlayers"
                  name="distributeFinalUnsoldPlayers"
                  type="checkbox"
                  className="form-check-input"
                  checked={formState.distributeFinalUnsoldPlayers === true}
                  onChange={handleInputChange}
                  disabled={!selectedTournamentId || isLoadingSettings}
                />
                <label className="form-check-label fw-semibold" htmlFor="distributeFinalUnsoldPlayers">
                  No Unsold Players
                </label>
                <div className="text-muted small">
                  After the final unsold round, remaining players will be distributed randomly and as evenly as possible across all teams.
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-light d-grid gap-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <p className="fw-semibold mb-0">Teams and leadership</p>
                    <p className="text-muted mb-0">
                      Set how many teams you plan to auction and capture rosters, owners, captains, vice captains, and wicket keepers.
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label fw-semibold mb-0" htmlFor="numberOfTeams">
                      Number of teams
                    </label>
                    <input
                      id="numberOfTeams"
                      type="number"
                      min="1"
                      max="12"
                      className="form-control"
                      style={{ maxWidth: '120px' }}
                      value={numberOfTeams}
                      onChange={handleTeamCountChange}
                      disabled={isLoadingSettings}
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="form-label fw-semibold mb-0">Teams</span>
                    <small className="text-muted">Switch between squads to update players and leadership roles.</small>
                  </div>
                  <div className="d-flex flex-nowrap overflow-auto" style={{ maxWidth: '100%' }}>
                    <ul className="nav nav-tabs flex-nowrap">
                      {teams.slice(0, numberOfTeams).map((team, index) => (
                        <li className="nav-item" key={`team-tab-${index}`}>
                          <button
                            type="button"
                            className={`nav-link ${activeTeamIndex === index ? 'active' : ''}`}
                            onClick={() => setActiveTeamIndex(index)}
                          >
                            {team.name || `Team ${index + 1}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {(() => {
                  const team = teams[activeTeamIndex];
                  if (!team) return null;

                  const availablePlayers = filteredPlayers(activeTeamIndex);
                  const ownerOptions = mappedPlayers;
                  const selectedPlayerIds = getTeamPlayerIds(team);
                  const selectedPlayerSet = new Set(selectedPlayerIds);
                  const roleOptions = selectedPlayerIds.map((playerId) => playerLookup.get(playerId)).filter(Boolean);

                  return (
                    <div key={`team-${activeTeamIndex}`} className="card border-0 shadow-sm">
                      <div className="card-body d-grid gap-3">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary-subtle text-primary-emphasis">Team {activeTeamIndex + 1}</span>
                            <h2 className="h6 mb-0">{team.name || `Team ${activeTeamIndex + 1}`}</h2>
                          </div>
                          {selectedPlayerIds.length ? (
                            <span className="badge bg-info-subtle text-info-emphasis">
                              {selectedPlayerIds.length} player{selectedPlayerIds.length === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </div>

                        <div className="row g-3">
                          <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold" htmlFor={`team-name-${activeTeamIndex}`}>
                              Team name
                            </label>
                            <input
                              id={`team-name-${activeTeamIndex}`}
                              type="text"
                              className="form-control"
                              value={team.name}
                              onChange={(event) => updateTeamField(activeTeamIndex, 'name', event.target.value)}
                              placeholder={`Team ${activeTeamIndex + 1}`}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`team-logo-${activeTeamIndex}`}>
                              Team logo
                            </label>
                            <input
                              id={`team-logo-${activeTeamIndex}`}
                              type="file"
                              className="form-control"
                              accept="image/*"
                              onChange={(event) => handleTeamLogoChange(activeTeamIndex, event.target.files?.[0])}
                            />
                            {team.logoUrl ? (
                              <div className="d-flex align-items-center gap-2 mt-2">
                                <img
                                  src={team.logoUrl}
                                  alt={`${team.name || `Team ${activeTeamIndex + 1}`} logo`}
                                  className="rounded border"
                                  style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => updateTeamField(activeTeamIndex, 'logoUrl', '')}
                                >
                                  Remove
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row g-3">
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`owner-${activeTeamIndex}`}>
                              Owner Player (Optional)
                            </label>
                            <select
                              id={`owner-${activeTeamIndex}`}
                              className="form-select"
                              value={team.ownerId || ''}
                              onChange={(event) => handleOwnerChange(activeTeamIndex, event.target.value)}
                              disabled={ownerOptions.length === 0 || isLoadingSettings}
                            >
                              <option value="">Select owner player</option>
                              {ownerOptions.map((owner) => {
                                const ownerId = owner.id;
                                return (
                                  <option key={ownerId} value={ownerId}>
                                    {owner.name}
                                  </option>
                                );
                              })}
                            </select>
                            <small className="text-muted">Choose optional owner player from tournament registered players. Owner can also be Captain, Vice Captain, Wicket Keeper, or a normal player.</small>
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`captain-${activeTeamIndex}`}>
                              Captain
                            </label>
                            <select
                              id={`captain-${activeTeamIndex}`}
                              className="form-select"
                              value={team.captain}
                              onChange={(event) => handleCaptainChange(activeTeamIndex, event.target.value)}
                              disabled={roleOptions.length === 0 || isLoadingPlayers}
                            >
                              <option value="">Select captain</option>
                              {roleOptions.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.name}
                                </option>
                              ))}
                            </select>
                            <small className="text-muted">Captain must be one of the selected team players.</small>
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`vice-captain-${activeTeamIndex}`}>
                              Vice Captain
                            </label>
                            <select
                              id={`vice-captain-${activeTeamIndex}`}
                              className="form-select"
                              value={team.viceCaptain || ''}
                              onChange={(event) => updateTeamField(activeTeamIndex, 'viceCaptain', event.target.value)}
                              disabled={roleOptions.length === 0 || isLoadingPlayers}
                            >
                              <option value="">Select vice captain</option>
                              {roleOptions.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.name}
                                </option>
                              ))}
                            </select>
                            <small className="text-muted">Vice Captain must be one of the selected team players.</small>
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`wicket-keeper-${activeTeamIndex}`}>
                              Wicket Keeper
                            </label>
                            <select
                              id={`wicket-keeper-${activeTeamIndex}`}
                              className="form-select"
                              value={team.wicketKeeper || ''}
                              onChange={(event) => updateTeamField(activeTeamIndex, 'wicketKeeper', event.target.value)}
                              disabled={roleOptions.length === 0 || isLoadingPlayers}
                            >
                              <option value="">Select wicket keeper</option>
                              {roleOptions.map((player) => (
                                <option key={player.id} value={player.id}>
                                  {player.name}
                                </option>
                              ))}
                            </select>
                            <small className="text-muted">Wicket Keeper must be one of the selected team players.</small>
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <label className="form-label fw-semibold" htmlFor={`initial-purse-${activeTeamIndex}`}>
                              Initial Purse
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">₹</span>
                              <input
                                id={`initial-purse-${activeTeamIndex}`}
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={team.initialPurse ?? formState.pursePerTeam ?? ''}
                                onChange={(event) => updateTeamField(activeTeamIndex, 'initialPurse', event.target.value)}
                              />
                            </div>
                            <small className="text-muted">Defaults to purse per team unless customized.</small>
                          </div>
                          <div className="col-12 col-md-6 col-lg-8">
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                              <div>
                                <p className="form-label fw-semibold mb-0">Players</p>
                                <small className="text-muted">Search tournament registered players and select this team's squad.</small>
                              </div>
                              <div className="input-group" style={{ maxWidth: '320px' }}>
                                <span className="input-group-text">Search</span>
                                <input
                                  type="search"
                                  className="form-control"
                                  placeholder="Name or email"
                                  value={teamSearchTerms[activeTeamIndex] || ''}
                                  onChange={(event) =>
                                    setTeamSearchTerms((previous) =>
                                      previous.map((term, idx) => (idx === activeTeamIndex ? event.target.value : term))
                                    )
                                  }
                                  disabled={isLoadingPlayers || mappedPlayers.length === 0}
                                />
                              </div>
                            </div>
                            <div className="border rounded" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                              {isLoadingPlayers ? (
                                <p className="text-muted mb-0 p-3">Loading players…</p>
                              ) : availablePlayers.length ? (
                                <ul className="list-unstyled mb-0">
                                  {availablePlayers.map((player) => {
                                    const initial = player.name?.charAt(0)?.toUpperCase?.() || '?';
                                    return (
                                      <li key={player.id} className="d-flex align-items-center gap-3 px-3 py-2 border-bottom">
                                        <div className="form-check m-0">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`retainer-${activeTeamIndex}-${player.id}`}
                                            checked={selectedPlayerSet.has(player.id)}
                                            onChange={() => toggleTeamPlayerSelection(activeTeamIndex, player.id)}
                                          />
                                        </div>
                                        <div className="flex-shrink-0">
                                          {player.image ? (
                                            <img
                                              src={player.image}
                                              alt={player.name}
                                              className="rounded-circle"
                                              style={{ width: '44px', height: '44px', objectFit: 'cover' }}
                                            />
                                          ) : (
                                            <div
                                              className="rounded-circle bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                                              style={{ width: '44px', height: '44px' }}
                                              aria-hidden="true"
                                            >
                                              <span className="fw-semibold">{initial}</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-grow-1">
                                          <p className="fw-semibold mb-0">{player.name}</p>
                                          {player.email ? <p className="text-muted small mb-0">{player.email}</p> : null}
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <p className="text-muted mb-0 p-3">
                                  No tournament registered players match your search.
                                </p>
                              )}
                            </div>
                            {playerError && <p className="text-danger small mb-0 mt-1">{playerError}</p>}
                            {selectedPlayerIds.length ? (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {selectedPlayerIds.map((retainerId) => {
                                  const player = mappedPlayers.find((candidate) => candidate.id === retainerId);
                                  return (
                                    <span key={retainerId} className="badge bg-primary-subtle text-primary-emphasis d-flex align-items-center gap-1">
                                      {player?.name || retainerId}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white btn-sm"
                                        aria-label="Remove player"
                                        onClick={() => toggleTeamPlayerSelection(activeTeamIndex, retainerId)}
                                      />
                                    </span>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-white d-grid gap-3">
                <div>
                  <p className="fw-semibold mb-1">Review teams</p>
                  <p className="text-muted mb-0">Confirm owner, leadership roles, player count, and purse before saving.</p>
                </div>
                <div className="row g-3">
                  {teams.slice(0, numberOfTeams).map((team, index) => {
                    const playerIds = getTeamPlayerIds(team);
                    return (
                      <div className="col-12 col-md-6 col-xl-4" key={`review-team-${index}`}>
                        <div className="border rounded p-3 h-100">
                          <p className="fw-semibold mb-2">{team.name || `Team ${index + 1}`}</p>
                          <p className="mb-1"><span className="text-muted">Owner:</span> {team.ownerId || team.ownerPlayerId ? getPlayerName(team.ownerId || team.ownerPlayerId) : 'Not selected'}</p>
                          <p className="mb-1"><span className="text-muted">Captain:</span> {team.captain ? getPlayerName(team.captain) : 'Not selected'}</p>
                          <p className="mb-1"><span className="text-muted">Vice Captain:</span> {team.viceCaptain ? getPlayerName(team.viceCaptain) : 'Not selected'}</p>
                          <p className="mb-1"><span className="text-muted">Wicket Keeper:</span> {team.wicketKeeper ? getPlayerName(team.wicketKeeper) : 'Not selected'}</p>
                          <p className="mb-1"><span className="text-muted">Players:</span> {playerIds.length}</p>
                          <p className="mb-0"><span className="text-muted">Purse:</span> {formatCurrency(team.initialPurse ?? formState.pursePerTeam)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-12 d-flex flex-wrap gap-3 align-items-center">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!selectedTournamentId || isSaving || isLoadingSettings}
              >
                {isSaving ? 'Saving settings…' : 'Save online auction'}
              </button>
              {statusMessage ? <span className="text-success">{statusMessage}</span> : null}
              {errorMessage ? <span className="text-danger">{errorMessage}</span> : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnlineAuctionSetupPage;
